import React, { useCallback, useEffect, useState } from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';
import {
    getProductToken,
    getUserInfo,
    removeTokenFromBrowser,
    storeTokenInBrowser
} from '../../auth/sso';
import AuthInterstitial from '../../components/Auth/AuthInterstitial';
import Button from '../../components/Button';
import ErrorMessage from '../../components/ErrorMessage';
import { BASE_PATH, LANDING_PAGE_BASE_URL } from '../../config';
import GoogleAnalyticsMetaDataObj from '../../helpers/googleAnalyticsHelpers/buildGoogleAnalyticsMetaDataObject';
import { sendMetadataEvent } from '../../helpers/googleAnalyticsHelpers/gaHelper';
import { useAuth } from '../../hooks/useAuth';
import { metricsApi } from '../../api/metricsApi';
import { isPick7iOSWebView } from '../../utils/authUtils';
import * as Styled from './styles';

const ERROR_TYPES = {
    PRODUCT_TOKEN_MISSING: {
        message:
            "We couldn't find your login session. Please try logging in again.",
        description: 'Login Error: productToken parameter missing',
        statusCode: 400
    },
    UUID_MISSING: {
        message:
            "We couldn't complete your login. Please try logging in again.",
        description: 'Login Error: uuid parameter missing',
        statusCode: 400
    },
    PRODUCT_TOKEN_FAILED: {
        message: 'Something went wrong while logging you in. Please try again.',
        description: 'Login Error: Failed to get product token',
        statusCode: 500
    },
    USER_INFO_FAILED: {
        message: "We couldn't load your account details. Please try again.",
        description: 'Login Error: Failed to get user info',
        statusCode: 500
    }
};

export const Callback = () => {
    const { authStore, dispatch } = useAuth();
    const [searchParams] = useSearchParams();

    const googleAnalyticsMetadata = new GoogleAnalyticsMetaDataObj(
        authStore.user.uki_customerid,
        authStore.loggedIn,
        authStore.user['com.sbgcore.upgradable']
    );

    useEffect(() => {
        sendMetadataEvent(googleAnalyticsMetadata, 'Auth');
    }, []);

    const [readyForRedirect, setReadyForRedirect] = useState(false);
    const [postLoginRedirectPath, setPostLoginRedirectPath] = useState('/');
    const [loginError, setLoginError] = useState(null);

    const logError = useCallback(errorType => {
        metricsApi.errorMetric(
            errorType.description,
            errorType.statusCode,
            errorType.description
        );
    }, []);

    const handleLogin = useCallback(async () => {
        setLoginError(null);

        if (authStore.loggedIn) {
            setReadyForRedirect(true);
            return;
        }

        let productToken = null;

        if (isPick7iOSWebView()) {
            // Handle iOS app webview authentication transfer
            productToken = searchParams.get('productToken');
            if (!productToken) {
                const errorType = ERROR_TYPES.PRODUCT_TOKEN_MISSING;
                logError(errorType);
                setLoginError(errorType);
                return;
            }
        } else {
            // Handle web authentication transfer
            const uuidParam = searchParams.get('uuid');
            if (!uuidParam) {
                const errorType = ERROR_TYPES.UUID_MISSING;
                logError(errorType);
                setLoginError(errorType);
                return;
            }

            // Fetch product token from ITV7 API with UUID
            productToken = await getProductToken(uuidParam);
            if (!productToken) {
                const errorType = ERROR_TYPES.PRODUCT_TOKEN_FAILED;
                logError(errorType);
                setLoginError(errorType);
                return;
            }
        }

        // Store product token
        storeTokenInBrowser(productToken);

        // Fetch user info with product token
        const user = await getUserInfo(productToken);
        if (user) {
            dispatch({ type: 'login', user, productToken });
            setReadyForRedirect(true);
        } else {
            const errorType = ERROR_TYPES.USER_INFO_FAILED;
            logError(errorType);
            removeTokenFromBrowser();
            setLoginError(errorType);
        }
    }, [authStore.loggedIn, dispatch, searchParams, logError]);

    useEffect(() => {
        setPostLoginRedirectPath(BASE_PATH);
        handleLogin();
    }, []);

    if (readyForRedirect) {
        return <Navigate to={postLoginRedirectPath} />;
    }

    if (loginError) {
        return (
            <AuthInterstitial>
                <Styled.ErrorContainer>
                    <ErrorMessage message={loginError.message} />
                    <Styled.ButtonWrapper>
                        <Button
                            type="secondary"
                            disabled={false}
                            buttonText="Restart Login"
                            onClick={() => {
                                window.location.href = LANDING_PAGE_BASE_URL;
                            }}
                        />
                    </Styled.ButtonWrapper>
                </Styled.ErrorContainer>
            </AuthInterstitial>
        );
    }

    return <AuthInterstitial>Logging You In - Please Wait</AuthInterstitial>;
};

Callback.propTypes = {};

export default Callback;
